/**
 * PURPOSE: Plazo de envío de uniformes por delegados del evento/deporte seleccionado (FTR-0014).
 * RESPONSIBILITIES: Mostrar el estado fail closed (sin plazo = configuración requerida y envío cerrado), los
 *   plazos activos que aplican (evento, deporte, extensiones por institución) y guardar/editar/desactivar con
 *   fecha, hora y minuto en hora de Lima; confirmar fechas pasadas y exigir motivo en extensiones. El reloj del
 *   servidor decide; aquí solo se previene.
 * COLLABORATORS: uniformOptionsService.js, uniformOptions.utils.js, CompanyUniformsTab.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/UniformDeadlinePanel.jsx
 */
import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";

import { disableUniformPolicy, saveUniformPolicy } from "../../services/uniformOptionsService.js";
import {
  POLICY_SCOPE_LABELS,
  describeDeadline,
  formatLimaDateTime,
  getUniformErrorMessage,
  isPastLimaDateTime,
} from "./uniformOptions.utils.js";

const EMPTY_FORM = { scopeType: "EVENT_SPORT", idinstitution: "", closesAtLima: "", reason: "" };

const UniformDeadlinePanel = ({ idevent, idsport, sportName, teams, summary, baseDeadline, onChanged }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const deadline = describeDeadline(baseDeadline);
  const institutions = useMemo(
    () =>
      teams
        .map((team) => team.institution)
        .filter(Boolean)
        .sort((left, right) => String(left.name).localeCompare(String(right.name))),
    [teams]
  );
  const institutionName = (idinstitution) =>
    institutions.find((institution) => Number(institution.id) === Number(idinstitution))?.name || `Institución ${idinstitution}`;

  const policies = (summary?.policies || []).filter(
    (policy) => policy.active && (policy.scopeType === "EVENT" || Number(policy.idsport) === Number(idsport))
  );

  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const isExtension = form.scopeType === "EVENT_SPORT_INSTITUTION";

  const edit = (policy) =>
    setForm({
      scopeType: policy.scopeType,
      idinstitution: policy.idinstitution ? String(policy.idinstitution) : "",
      closesAtLima: policy.closesAtLima || "",
      reason: policy.reason || "",
    });

  const submit = async (event) => {
    event.preventDefault();
    if (!form.closesAtLima) return toast.error("Ingresa la fecha y la hora límite.");
    if (isExtension && !form.idinstitution) return toast.error("Elige la institución de la extensión.");
    if (isExtension && !form.reason.trim()) return toast.error("La extensión requiere un motivo.");
    if (
      isPastLimaDateTime(form.closesAtLima) &&
      !window.confirm("Esa fecha y hora ya pasaron (hora de Lima): el envío quedará cerrado para ese alcance. ¿Guardar igualmente?")
    ) {
      return undefined;
    }
    setSaving(true);
    try {
      const result = await saveUniformPolicy(idevent, {
        scopeType: form.scopeType,
        ...(form.scopeType !== "EVENT" ? { idsport: Number(idsport) } : {}),
        ...(isExtension ? { idinstitution: Number(form.idinstitution) } : {}),
        closesAtLima: form.closesAtLima,
        ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
      });
      toast.success("Plazo guardado.");
      setForm(EMPTY_FORM);
      onChanged(result.summary);
    } catch (error) {
      toast.error(getUniformErrorMessage(error, "No se pudo guardar el plazo."));
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  const disable = async (policy) => {
    const reason = window.prompt("Se desactivará este plazo. Motivo (opcional):", "");
    if (reason === null) return;
    try {
      const result = await disableUniformPolicy(idevent, policy.id, reason);
      toast.success("Plazo desactivado.");
      onChanged(result.summary);
    } catch (error) {
      toast.error(getUniformErrorMessage(error, "No se pudo desactivar el plazo."));
    }
  };

  const scopeText = (policy) =>
    policy.scopeType === "EVENT_SPORT_INSTITUTION"
      ? `Extensión · ${institutionName(policy.idinstitution)}`
      : policy.scopeType === "EVENT_SPORT"
        ? `${sportName || "Deporte"}`
        : POLICY_SCOPE_LABELS.EVENT;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-bold text-slate-800">Plazo de envío de delegados</h3>
        <p className="text-xs text-slate-500">Fecha, hora y minuto en hora de Lima (America/Lima). Sin plazo vigente, el envío está cerrado.</p>
      </div>

      <div
        role="status"
        className={`mt-3 rounded-lg border px-4 py-3 text-sm font-semibold ${
          deadline.open ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"
        }`}
      >
        {sportName ? `${sportName}: ` : ""}
        {deadline.text}
        {!deadline.open && (
          <span className="mt-1 block text-xs font-normal">
            Los delegados pueden ver sus opciones, pero no crear, editar, reordenar ni enviar. La administración puede corregir y queda auditada.
          </span>
        )}
      </div>

      {policies.length > 0 && (
        <ul className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm">
          {policies.map((policy) => (
            <li key={policy.id} className="flex flex-col gap-2 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-semibold text-slate-700">{scopeText(policy)}</p>
                <p className="text-xs text-slate-500">
                  Cierra {formatLimaDateTime(policy.closesAtLima)}
                  {policy.reason ? ` · ${policy.reason}` : ""}
                </p>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => edit(policy)} className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                  Editar
                </button>
                <button type="button" onClick={() => disable(policy)} className="rounded-lg border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50">
                  Desactivar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-xs font-semibold text-slate-600">
          Alcance
          <select value={form.scopeType} onChange={set("scopeType")} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm font-normal">
            <option value="EVENT">{POLICY_SCOPE_LABELS.EVENT}</option>
            <option value="EVENT_SPORT">{sportName ? `Solo ${sportName}` : POLICY_SCOPE_LABELS.EVENT_SPORT}</option>
            <option value="EVENT_SPORT_INSTITUTION">{POLICY_SCOPE_LABELS.EVENT_SPORT_INSTITUTION}</option>
          </select>
        </label>
        {isExtension && (
          <label className="text-xs font-semibold text-slate-600">
            Institución
            <select value={form.idinstitution} onChange={set("idinstitution")} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm font-normal">
              <option value="">Seleccione</option>
              {institutions.map((institution) => (
                <option key={institution.id} value={institution.id}>
                  {institution.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="text-xs font-semibold text-slate-600">
          Fecha y hora límite (Lima)
          <input
            type="datetime-local"
            value={form.closesAtLima}
            onChange={set("closesAtLima")}
            step={60}
            className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm font-normal"
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Motivo{isExtension ? " *" : " (opcional)"}
          <input type="text" value={form.reason} onChange={set("reason")} maxLength={255} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm font-normal" />
        </label>
        <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-4">
          <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-slate-300">
            {saving ? "Guardando..." : "Guardar plazo"}
          </button>
          {(form.closesAtLima || form.reason) && (
            <button type="button" onClick={() => setForm(EMPTY_FORM)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
              Limpiar
            </button>
          )}
        </div>
      </form>
    </section>
  );
};

export default UniformDeadlinePanel;
